import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Injector, OnDestroy } from '@angular/core';
import {
  DwConfigService,
  DwConfigServiceToken,
  DwEventService,
  DwLoggingService,
  DwSecurityUserService
} from '@devwareapps/devware-cap';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { DwPubSubService } from '../../z-devware/pubsub/dw-pub-sub-service';
import { DwPubSubEventTopics } from '../../z-devware/pubsub/models/dw-pub-sub-event-topics.model';
import { DwPubSubMessage } from '../../z-devware/pubsub/models/dw-pub-sub-message.model';
import {
  LessonVideoPlayback,
  ModuleVideoPlayback,
  VideoLibraryPlaybackConfigurationChanged
} from '../models/video-playback.model';

interface ModulePlaybackCacheEntry {
  key: string;
  studentCourseId: number;
  courseModuleId: number;
  subject: ReplaySubject<ModuleVideoPlayback>;
  bundle?: ModuleVideoPlayback;
  loading: boolean;
  active: boolean;
  refreshTimer?: any;
}

@Injectable({ providedIn: 'root' })
export class ModuleVideoPlaybackService implements OnDestroy {
  private readonly videoApi: string;
  private readonly cache = new Map<string, ModulePlaybackCacheEntry>();
  private readonly subscriptions = new Subscription();
  private readonly configurationChangedSubject = new Subject<VideoLibraryPlaybackConfigurationChanged>();
  private dwPubSubService?: DwPubSubService;
  private playbackConfigurationSubscription?: Subscription;

  readonly configurationChanged$ = this.configurationChangedSubject.asObservable();

  // Web now supports Bunny's CDN-authenticated HLS source. The server still
  // controls rollout per video library through its playback mode.
  private readonly supportsCdnAuthenticatedHlsPlayback = true;

  constructor(
    @Inject(DwConfigServiceToken) configService: DwConfigService,
    private http: HttpClient,
    private dwEventService: DwEventService,
    private dwLoggingService: DwLoggingService,
    private dwSecurityUserService: DwSecurityUserService,
    private injector: Injector
  ) {
    this.videoApi = `${configService.coreConfig.apiRoot}/aviator-online/videos`;

    this.subscriptions.add(
      this.dwSecurityUserService.securityContextChanged().subscribe(() => this.clear())
    );
  }

  ngOnDestroy(): void {
    this.clear();
    this.subscriptions.unsubscribe();
    this.configurationChangedSubject.complete();
  }

  getModulePlayback(
    studentCourseId: number,
    courseModuleId: number,
    forceRefresh: boolean = false
  ): Observable<ModuleVideoPlayback> {
    const entry = this.getOrCreateEntry(studentCourseId, courseModuleId);

    if (forceRefresh || !entry.bundle) {
      this.load(entry);
    } else if (this.shouldRefresh(entry.bundle)) {
      this.load(entry);
    }

    return entry.subject.asObservable();
  }

  preload(studentCourseId: number, courseModuleId: number): void {
    this.getModulePlayback(studentCourseId, courseModuleId);
  }

  getLessonPlayback(
    studentCourseId: number,
    courseModuleId: number,
    courseLessonId: number
  ): Observable<LessonVideoPlayback | null> {
    return this.getModulePlayback(studentCourseId, courseModuleId)
      .pipe(map(bundle => bundle.Videos.find(video => video.LessonId === courseLessonId) || null));
  }

  refresh(studentCourseId: number, courseModuleId: number): void {
    const entry = this.getOrCreateEntry(studentCourseId, courseModuleId);
    this.load(entry, true);
  }

  setActiveModule(studentCourseId: number, courseModuleId: number): void {
    this.cache.forEach(entry => {
      entry.active = entry.studentCourseId === studentCourseId && entry.courseModuleId === courseModuleId;
      if (!entry.active) {
        this.clearRefreshTimer(entry);
      }
    });

    const activeEntry = this.getOrCreateEntry(studentCourseId, courseModuleId);
    activeEntry.active = true;
    if (activeEntry.bundle) {
      this.scheduleRefresh(activeEntry);
    }
  }

  clearActiveModule(): void {
    this.cache.forEach(entry => {
      entry.active = false;
      this.clearRefreshTimer(entry);
    });
  }

  clear(): void {
    this.cache.forEach(entry => this.clearRefreshTimer(entry));
    this.cache.clear();
  }

  private getOrCreateEntry(studentCourseId: number, courseModuleId: number): ModulePlaybackCacheEntry {
    const key = `${studentCourseId}:${courseModuleId}`;
    let entry = this.cache.get(key);

    if (!entry) {
      entry = {
        key,
        studentCourseId,
        courseModuleId,
        subject: new ReplaySubject<ModuleVideoPlayback>(1),
        loading: false,
        active: false
      };
      this.cache.set(key, entry);
    }

    return entry;
  }

  private load(entry: ModulePlaybackCacheEntry, forceRefresh: boolean = false): void {
    if (entry.loading && !forceRefresh) {
      return;
    }

    entry.loading = true;
    const url = `${this.videoApi}/modules/${entry.courseModuleId}/playback`;
    const params = new HttpParams()
      .set('studentCourseId', entry.studentCourseId.toString())
      .set('supportsCdnAuthenticatedHlsPlayback', this.supportsCdnAuthenticatedHlsPlayback.toString());

    this.http.get<ModuleVideoPlayback>(url, { params }).subscribe(
      bundle => {
        entry.loading = false;
        entry.bundle = bundle;
        entry.subject.next(bundle);
        this.startPlaybackConfigurationSubscription();
        this.scheduleRefresh(entry);
      },
      error => {
        entry.loading = false;
        this.dwLoggingService.logError(
          `Unable to load module video playback for module ${entry.courseModuleId}`,
          error,
          null,
          true
        );
      }
    );
  }

  private scheduleRefresh(entry: ModulePlaybackCacheEntry): void {
    this.clearRefreshTimer(entry);
    if (!entry.active || !entry.bundle?.RefreshAfterUtc) {
      return;
    }

    const refreshDelayMs = Math.max(0, new Date(entry.bundle.RefreshAfterUtc).getTime() - Date.now());
    const maxTimeoutMs = 2147483647;

    entry.refreshTimer = setTimeout(
      () => this.load(entry),
      Math.min(refreshDelayMs, maxTimeoutMs)
    );
  }

  private shouldRefresh(bundle: ModuleVideoPlayback): boolean {
    return !!bundle?.RefreshAfterUtc && new Date(bundle.RefreshAfterUtc).getTime() <= Date.now();
  }

  private clearRefreshTimer(entry: ModulePlaybackCacheEntry): void {
    if (entry.refreshTimer) {
      clearTimeout(entry.refreshTimer);
      entry.refreshTimer = null;
    }
  }

  private startPlaybackConfigurationSubscription(): void {
    if (this.playbackConfigurationSubscription) {
      return;
    }

    // Resolving DwPubSubService starts its connection. Keep this lazy so the
    // module URL request remains the first network operation in video playback.
    this.dwPubSubService = this.injector.get(DwPubSubService);
    this.playbackConfigurationSubscription = this.dwEventService.getEventData<DwPubSubMessage>(
      DwPubSubEventTopics.messageReceived(DwPubSubEventTopics.videoLibraryPlaybackConfigurationChanged)
    ).subscribe(message => this.handleConfigurationChanged(message));
    this.subscriptions.add(this.playbackConfigurationSubscription);
  }

  private handleConfigurationChanged(message: DwPubSubMessage): void {
    let change: VideoLibraryPlaybackConfigurationChanged;

    try {
      change = JSON.parse(message?.Payload || '{}') as VideoLibraryPlaybackConfigurationChanged;
    } catch (error) {
      this.dwLoggingService.logError('Unable to parse video playback configuration change', error, null, true);
      return;
    }

    if (!change?.VideoLibraryId) {
      return;
    }

    this.configurationChangedSubject.next(change);

    this.cache.forEach(entry => {
      const usesLibrary = entry.bundle?.Videos?.some(video => video.VideoLibraryId === change.VideoLibraryId);
      if (usesLibrary) {
        if (entry.active) {
          this.load(entry, true);
        } else {
          this.clearRefreshTimer(entry);
          this.cache.delete(entry.key);
        }
      }
    });

    this.dwPubSubService?.emitDebug('Info', 'Video playback configuration changed', change);
  }
}
